import { AppBar, Toolbar, Box } from '@mui/material';
import { useRef } from 'react';
import { App, BannerAlertMessagesCollection, BannerMobileAlertMessagesCollection, Header, Maybe } from '@/graphql/__generated/sdk';
import { logoStyles, headerStyles, navigationStyles, navigationLinkStyles, toolbarStyles, toolbarWrapStyles } from './HeaderDesktop.styles';
import GroupNavList from '../GroupNavList/GroupNavList';
import LocationNavBar from '../LocationNavBar/LocationNavBar';
import NavigationBar from '../NavigationBar/NavigationBar';
import WelcomeBackBanner from '../WelcomeBackBanner/WelcomeBackBanner';
import Banner from '@shared-components/components/Banner/Banner';
import ImageWrapper from '@shared-components/components/ImageWrapper';
import TAGLink from '@shared-components/components/TAGLink';
import useIsLoggedIn from '@src/hooks/useIsLoggedIn';
interface IHeaderDesktop {
  headerData: App['header'];
  logoUrl?: Maybe<string>;
  logoHref: string;
  displayHeaderGroupMenu?: boolean;
}
export function bannerConfigExtractor(headerData?: Maybe<Header>, isMobile?: boolean): {
  topAlertBannerMessages: BannerAlertMessagesCollection | BannerMobileAlertMessagesCollection;
  bottomAlertBannerMessages: BannerAlertMessagesCollection | BannerMobileAlertMessagesCollection;
  showTopAlertBannerMessages: boolean;
  showBottomAlertBannerMessages: boolean;
} {
  let topAlertBannerMessages;
  let bottomAlertBannerMessages;
  const {
    topAlertBanner,
    bottomAlertBanner
  } = headerData ?? {};
  if (isMobile) {
    topAlertBannerMessages = topAlertBanner?.mobileAlertMessagesCollection ?? {} as BannerMobileAlertMessagesCollection;
    bottomAlertBannerMessages = bottomAlertBanner?.mobileAlertMessagesCollection ?? {} as BannerMobileAlertMessagesCollection;
  } else {
    topAlertBannerMessages = topAlertBanner?.alertMessagesCollection ?? {} as BannerAlertMessagesCollection;
    bottomAlertBannerMessages = bottomAlertBanner?.alertMessagesCollection ?? {} as BannerAlertMessagesCollection;
  }
  const showTopAlertBannerMessages = topAlertBannerMessages && topAlertBannerMessages.items?.length > 0;
  const showBottomAlertBannerMessages = bottomAlertBannerMessages && bottomAlertBannerMessages.items?.length > 0;
  return {
    topAlertBannerMessages,
    bottomAlertBannerMessages,
    showTopAlertBannerMessages,
    showBottomAlertBannerMessages
  };
}
export default function HeaderDesktop({
  logoUrl,
  logoHref = '/',
  headerData,
  displayHeaderGroupMenu
}: Readonly<IHeaderDesktop>) {
  const {
    topAlertBannerMessages,
    bottomAlertBannerMessages,
    showTopAlertBannerMessages,
    showBottomAlertBannerMessages
  } = bannerConfigExtractor(headerData);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const {
    isLoggedIn,
    firstName,
    checked
  } = useIsLoggedIn();
  return <AppBar data-testid="header_desktop_with_location_nav" sx={headerStyles} data-sentry-element="AppBar" data-sentry-component="HeaderDesktop" data-sentry-source-file="HeaderDesktop.tsx">
			{showTopAlertBannerMessages && <Banner dataTestId="header_desktop_top_alert_banner" variant={'Alert Banner'} alertMessagesCollection={topAlertBannerMessages as BannerAlertMessagesCollection} />}
			<Toolbar id="header_desktop_toolbar" data-testid="header_desktop_toolbar" disableGutters sx={toolbarStyles} ref={toolbarRef} data-sentry-element="Toolbar" data-sentry-source-file="HeaderDesktop.tsx">
				<Box sx={toolbarWrapStyles} data-sentry-element="Box" data-sentry-source-file="HeaderDesktop.tsx">
					<TAGLink href={logoHref} data-sentry-element="TAGLink" data-sentry-source-file="HeaderDesktop.tsx">
						<ImageWrapper src={logoUrl} width="unset" height="unset" sx={logoStyles} data-testid="img_logo_desktop_with_location_nav" alt="brand logo" data-sentry-element="ImageWrapper" data-sentry-source-file="HeaderDesktop.tsx" />
					</TAGLink>
					{displayHeaderGroupMenu ? <GroupNavList dataTestId="header_with_location_nav_main_navigation_group" headerData={headerData} toolbarRef={toolbarRef} /> : <NavigationBar dataTestId="header_with_location_nav_main_navigation_bar[1]" navigationItems={headerData?.navigationLinksCollection} navigationSx={navigationStyles} navigationLinkSx={navigationLinkStyles} />}
					{(!checked || !isLoggedIn) && <NavigationBar dataTestId="header_with_location_nav_main_navigation_bar[2]" navigationItems={headerData?.secondaryNavigationLinksCollection} navigationSx={navigationStyles} navigationLinkSx={navigationLinkStyles} linkColor="text.interactive" fontWeight={700} showIconText={true} />}
					{checked && isLoggedIn && <WelcomeBackBanner firstName={firstName} />}
				</Box>
			</Toolbar>
			<LocationNavBar schedulingButtonText={headerData?.schedulingButtonText} locationBarFindOfficeUrl={headerData?.locationBarFindOfficeUrl} schedulingButtonUrl={headerData?.schedulingButtonUrl} searchFieldTitle={headerData?.searchFieldTitle} data-sentry-element="LocationNavBar" data-sentry-source-file="HeaderDesktop.tsx" />
			{showBottomAlertBannerMessages && <Banner dataTestId="header_desktop_bottom_alert_banner" variant={'Alert Banner'} alertMessagesCollection={bottomAlertBannerMessages as BannerAlertMessagesCollection} />}
		</AppBar>;
}